import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  filled,
  spf,
  map_to_arr,
  data_nullify,
  arr_wrap,
  safely_encode_to_json,
  arr_filter,
  isId,
} from './../shared/utils/common';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class RelateSearchService {
  private readonly _cache = new Map<string, _RelateCacheEntry>();

  constructor(
    private readonly _api: ApiService,
  ) {}

  search(module: string, opts: {
    term?: string;
    record_id?: string | string[];
    filter?: Record<string, any>;
    with_additional_filter?: boolean;
  } = {}): Observable<FieldmagicRelateOption[]> {
    const data: Record<string, any> = {
      limit: 10,
      term: data_nullify(opts.term),
    };

    if (filled(opts.filter)) {
      data['filter'] = opts.filter;
    }

    if (filled(opts.record_id) && opts.record_id !== '__ALL__') {
      data['ids'] = arr_filter(arr_wrap(opts.record_id), {
        predicate: (id) => isId(id),
      });
    }

    if (opts.with_additional_filter) {
      data['additional_filter'] = true;
    }

    const key = spf('%s:%s', {
      args: [module, btoa(safely_encode_to_json(data))],
    });

    /// attempt to use cache
    const cached = this._cache.get(key);

    if (cached && filled(cached.inflight)) {
      return cached.inflight;
    }

    const request$ = this._api.callPrivate$({
      path: '/record/relate',
      body: {
        module,
        data,
      },
      onSuccessResponse: (response) => map_to_arr(response),
    });

    this._cache.set(key, {
      inflight: request$,
      expires_in: Date.now() + _RELATE_CACHE_TTL,
    });

    return request$;
  }
}

const _RELATE_CACHE_TTL = 5 * 60 * 1000; /// 5 minutes;

type _RelateCacheEntry = {
  expires_in: number;
  inflight?: Observable<FieldmagicRelateOption[]>;
  results?: FieldmagicRelateOption[];
};

export type FieldmagicRelateOption<T = any> = {
  id: string;
  text: string;
} & T;

